import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { notifyError } from '../lib/notify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InquiryChat = ({ serviceId, providerId, providerName, serviceName, user, onClose }) => {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [inquiryId, setInquiryId] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [oldestCursor, setOldestCursor] = useState(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [showJumpToLatest, setShowJumpToLatest] = useState(false);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const prependingRef = useRef(false);

  useEffect(() => {
    checkExistingInquiry();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceId]);

  useEffect(() => {
    if (prependingRef.current) { prependingRef.current = false; return; }
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const jumpToLatest = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    setShowJumpToLatest(false);
  };

  const handleScroll = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    setShowJumpToLatest(distanceFromBottom > 200);
  };

  const checkExistingInquiry = async () => {
    try {
      const response = await axios.get(`${API}/inquiries`);
      const existing = response.data.find(i => i.service_id === serviceId);
      if (existing) {
        setInquiryId(existing.id);
        fetchMessages(existing.id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error checking inquiries:', error);
      setLoading(false);
    }
  };

  const fetchMessages = async (id) => {
    try {
      const response = await axios.get(`${API}/inquiries/${id}?paginated=true&msg_limit=50`);
      setMessages(response.data.messages || []);
      setHasMoreMessages(!!response.data.messages_has_more);
      setOldestCursor(response.data.oldest_cursor || null);
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOlderMessages = async () => {
    if (!inquiryId || !oldestCursor || loadingOlder) return;
    setLoadingOlder(true);
    prependingRef.current = true;
    try {
      const response = await axios.get(
        `${API}/inquiries/${inquiryId}?paginated=true&msg_limit=50&before=${encodeURIComponent(oldestCursor)}`
      );
      const older = response.data.messages || [];
      setMessages((prev) => [...older, ...prev]);
      setHasMoreMessages(!!response.data.messages_has_more);
      setOldestCursor(response.data.oldest_cursor || null);
    } catch (error) {
      console.error('Error loading older messages:', error);
      prependingRef.current = false;
    } finally {
      setLoadingOlder(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      if (inquiryId) {
        // Reply to existing inquiry
        await axios.post(`${API}/inquiries/${inquiryId}/message`, { message: newMessage });
        fetchMessages(inquiryId);
      } else {
        // Create new inquiry
        const response = await axios.post(
          `${API}/services/${serviceId}/inquiry?message=${encodeURIComponent(newMessage)}`
        );
        setInquiryId(response.data.inquiry_id);
        fetchMessages(response.data.inquiry_id);
      }
      setNewMessage('');
    } catch (error) {
      console.error('Error sending message:', error);
      notifyError(error, 'Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-testid="inquiry-chat-modal">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="px-4 py-3 border-b bg-gradient-to-r from-orange-500 to-orange-600 rounded-t-lg">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-lg font-bold text-white">Inquiry</h3>
              <p className="text-white text-opacity-80 text-sm">{serviceName}</p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200"
              data-testid="close-inquiry-chat"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Messages */}
        <div className="relative flex-1 flex flex-col min-h-[300px]">
        <div ref={scrollContainerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="inquiry-messages">
          {loading ? (
            <div className="flex items-center justify-center h-full text-gray-500">
              Loading...
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <p className="mb-2">Start a conversation with {providerName}</p>
              <p className="text-sm">Ask about availability, pricing, or any questions about this service.</p>
            </div>
          ) : (
            <>
              {hasMoreMessages && (
                <div className="text-center pb-2">
                  <button
                    onClick={loadOlderMessages}
                    disabled={loadingOlder}
                    className="text-xs text-orange-600 hover:text-orange-700 font-medium disabled:opacity-50"
                    data-testid="load-older-messages"
                  >
                    {loadingOlder ? 'Loading…' : 'Load older messages'}
                  </button>
                </div>
              )}
              {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[70%] rounded-lg px-4 py-2 ${
                    msg.sender_id === user?.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-800'
                  }`}
                >
                  <p className="text-sm font-semibold mb-1">
                    {msg.sender_id === user?.id ? 'You' : msg.sender_name}
                  </p>
                  <p>{msg.message}</p>
                  <p className={`text-xs mt-1 ${msg.sender_id === user?.id ? 'text-blue-200' : 'text-gray-500'}`}>
                    {new Date(msg.created_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>
              ))}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
        {showJumpToLatest && (
          <button
            type="button"
            onClick={jumpToLatest}
            className="absolute bottom-4 right-4 bg-orange-500 hover:bg-orange-600 text-white rounded-full p-2 shadow-lg transition active:scale-95"
            data-testid="jump-to-latest"
            aria-label="Jump to latest messages"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </button>
        )}
        </div>

        {/* Message Input */}
        <form onSubmit={sendMessage} className="p-4 border-t">
          <div className="flex gap-2">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="inquiry-message-input"
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="px-4 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="send-inquiry-message"
            >
              {sending ? '...' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InquiryChat;
